import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  agoraPlatformAPI,
  assistanceAPI,
  contributionsAPI,
  dashboardAPI,
  membersAPI,
  messagingPlatformAPI,
  peoplePlatformAPI,
} from '@/services';
import { ledgerApi } from '@/services/api';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../hooks/usePermissions';
import { useModules } from '../contexts/ModulesContext';
import { isBoardOfficerRole, isMemberEffectiveRole } from '../utils/iamRoles';
import { resolveMemberProfileId, resolveNextMemberProfileId } from '../utils/memberProfileId';
import { isAdhesionContributionMonth } from '../utils/memberDashboardPersonalStats';
import DashboardAlerts from '../components/DashboardAlerts';
import { TreasurerFinancialWidget, SecretaryWidget, PresidentOverviewWidget, TrendsWidget, ContributionsStatsWidget, LedgerPlatformWidget } from '../components/DashboardWidgets';
import MemberDashboard from '../components/MemberDashboard';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const COLORS = ['#6366f1', '#10b981', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6'];

const QUICK_ACCESS_DEFS = [
  {
    id: 'members',
    nameKey: 'nav.members',
    href: '/members',
    icon: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z',
    color: 'blue',
    permission: ['members.view', 'members.view_own'],
  },
  {
    id: 'contributions',
    nameKey: 'nav.contributions',
    href: '/contributions',
    icon: 'M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z',
    color: 'emerald',
    permission: ['contributions.view', 'contributions.view_own'],
  },
  {
    id: 'finances',
    nameKey: 'nav.finances',
    href: '/my-fund/financial',
    /** Catalogue modules : `component.route` reste `/financial`. */
    moduleRoute: '/financial',
    icon: 'M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
    color: 'green',
    permission: ['financial.view', 'financial.view_own'],
  },
  {
    id: 'documents',
    nameKey: 'nav.documents',
    href: '/documents',
    icon: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z',
    color: 'purple',
    permission: ['documents.view', 'documents.view_public'],
  },
  {
    id: 'calendar',
    nameKey: 'nav.calendar',
    href: '/calendar',
    icon: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z',
    color: 'pink',
    permission: ['events.view', 'events.view_public'],
  },
];

const Home = () => {
  const { t, i18n } = useTranslation('common');
  const localeFmt = (i18n.resolvedLanguage || i18n.language || 'fr').startsWith('en') ? 'en-US' : 'fr-FR';
  const quickAccessItems = useMemo(
    () => QUICK_ACCESS_DEFS.map((item) => ({ ...item, name: t(item.nameKey) })),
    [t, i18n.language],
  );
  const [stats, setStats] = useState(null);
  const [personalStats, setPersonalStats] = useState(null);
  /** Dossiers d’allocation (membre) : chargé avec l’accueil ; `loading` évite un flash 0 avant la réponse API. */
  const [memberAssistanceCases, setMemberAssistanceCases] = useState([]);
  const [memberAssistanceLoading, setMemberAssistanceLoading] = useState(true);
  const [memberInternalActivities, setMemberInternalActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const { role, hasPermission } = usePermissions();
  const { isRouteEnabled } = useModules();
  const isMember = isMemberEffectiveRole(role);
  const hasMemberProfileLink = !!(user?.member_id || user?.person_id);
  /** Officers liés à une fiche membre: entrée via MemberDashboard, puis menus manager. */
  const useMemberPortalHome = isMember || (isBoardOfficerRole(role) && hasMemberProfileLink);
  /** Tableau de bord financier agrégé : stats globales (président / admin) ou stats financières (trésorier). */
  const canSeeOrgFinancialDashboard =
    hasPermission('dashboard.stats') || hasPermission('dashboard.stats_financial');
  /** Widget trésorerie : profil « trésorier » (export sans stats org complètes ; évite org_admin / saas). */
  const canSeeTreasurerWidget =
    hasPermission('financial.export') && !hasPermission('dashboard.stats');
  /** Widget secrétaire : réunions/documents sans vue financière complète (évite président/trésorier). */
  const canSeeSecretaryWidget =
    !isMember &&
    hasPermission('meetings.publish') &&
    !hasPermission('financial.view');
  /** Vue « bureau » type président (stats org, pas seulement perso trésorier). */
  const canSeePresidentOverviewWidget = hasPermission('dashboard.stats');

  const getList = (payload, key) => {
    if (Array.isArray(payload)) return payload;
    if (payload && Array.isArray(payload[key])) return payload[key];
    return [];
  };

  // Filter quick access items based on permissions and enabled modules
  const filteredQuickAccess = quickAccessItems.filter(item => {
    // Hide "Membres" for members
    if (item.id === 'members' && isMember) {
      return false;
    }
    
    // Vérifier les permissions
    let hasRequiredPermission = false;
    if (Array.isArray(item.permission)) {
      hasRequiredPermission = item.permission.some(perm => hasPermission(perm));
    } else {
      hasRequiredPermission = hasPermission(item.permission);
    }
    
    if (!hasRequiredPermission) {
      return false;
    }
    
    // Vérifier si le module/composant est activé
      // Aucun passe-droit admin : on respecte strictement les modules activés.
    const routeForModule = item.moduleRoute || item.href;
    if (routeForModule && routeForModule !== '/') {
      return isRouteEnabled(routeForModule);
    }
    
    return true;
  });

  useEffect(() => {
    const loadStats = async () => {
      try {
        const now = new Date();
        const thisMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
        const prev = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        const prevMonth = `${prev.getFullYear()}-${String(prev.getMonth() + 1).padStart(2, '0')}`;

        if (useMemberPortalHome) {
          setMemberAssistanceLoading(true);
          // Load personal stats and global financial stats for members
          const [personalResponse, financialResponse, peopleRes, ledgerBalRes, ledgerStatsRes, agoraActivitiesRes] = await Promise.all([
            dashboardAPI.getPersonalStats(),
            dashboardAPI.getStats(),
            peoplePlatformAPI.getStatsSummary().catch(() => ({ data: {} })),
            ledgerApi.getOrgBalance().catch(() => ({ data: {} })),
            ledgerApi.getStatsSummary().catch(() => ({ data: {} })),
            agoraPlatformAPI.listActivities({ limit: 100 }).catch(() => ({ data: {} })),
          ]);
          let memberPayload = personalResponse.data;
          if (
            memberPayload &&
            (!Array.isArray(memberPayload.contribution_history) ||
              memberPayload.contribution_history.length === 0)
          ) {
            const tryPersonId =
              memberPayload.person_id ||
              memberPayload.member_id ||
              user?.person_id ||
              user?.member_id;
            if (tryPersonId) {
              try {
                const cr = await contributionsAPI.getByPerson(String(tryPersonId));
                const arr = Array.isArray(cr.data) ? cr.data : [];
                if (arr.length > 0) {
                  const history = [...arr]
                    .sort((a, b) => String(b.month || '').localeCompare(String(a.month || '')))
                    .slice(0, 24)
                    .map((c) => ({
                      id: c.id,
                      _id: c._id,
                      month: c.month,
                      amount: Number(c.amount || 0),
                      status: String(c.status || 'unknown').toLowerCase(),
                      paid_at: c.paid_at,
                      created_at: c.created_at,
                      updated_at: c.updated_at,
                    }));
                  const paidSt = new Set(['completed', 'paid', 'approved']);
                  const pendingSt = new Set(['pending', 'unpaid', 'awaiting', 'draft']);
                  let adhesion_fees = 0;
                  let regular_contributions = 0;
                  for (const c of arr) {
                    if (!paidSt.has(String(c.status || '').toLowerCase())) continue;
                    const amt = Number(c.amount || 0);
                    if (isAdhesionContributionMonth(c.month)) adhesion_fees += amt;
                    else regular_contributions += amt;
                  }
                  const unpaidRows = arr.filter((c) => pendingSt.has(String(c.status || '').toLowerCase()));
                  const apiTotal = Number(memberPayload?.total_contributions || 0);
                  const fromArr = adhesion_fees + regular_contributions;
                  const total_contributions = apiTotal > fromArr ? apiTotal : fromArr;
                  memberPayload = {
                    ...memberPayload,
                    contribution_history: history,
                    unpaid_contributions: unpaidRows.map((c) => ({
                      month: c.month || c.created_at,
                      amount: Number(c.amount || 0),
                      status: String(c.status || '').toLowerCase(),
                    })),
                    adhesion_fees,
                    total_contributions,
                    regular_contributions: Math.max(0, total_contributions - adhesion_fees),
                  };
                }
              } catch {
                /* endpoint optionnel ou filtre IAM : on garde le payload API */
              }
            }
          }
          const profileIdForMemberNumber =
            memberPayload?.person_id ||
            memberPayload?.member_id ||
            user?.person_id ||
            user?.member_id;
          if (profileIdForMemberNumber && !String(memberPayload?.member_number || '').trim()) {
            try {
              const peopleProfileRes = await peoplePlatformAPI.getById(String(profileIdForMemberNumber));
              const p = peopleProfileRes?.data;
              const mn = String(p?.member_number || p?.metadata?.profile?.member_number || '').trim();
              if (mn) {
                memberPayload = { ...memberPayload, member_number: mn };
              }
            } catch {
              /* profil People optionnel */
            }
          }
          const statsProfileId = String(
            memberPayload?.person_id || memberPayload?.member_id || user?.person_id || user?.member_id || ''
          ).trim();
          if (statsProfileId) {
            try {
              const { data: memSt } = await membersAPI.getStats(statsProfileId);
              if (memSt && typeof memSt === 'object') {
                const paidM = Number(memSt.paid_months);
                const unpaidM = Number(memSt.unpaid_months);
                memberPayload = {
                  ...memberPayload,
                  ...(Number.isFinite(paidM) && paidM >= 0 ? { paid_months: paidM } : {}),
                  ...(Number.isFinite(unpaidM) && unpaidM >= 0 ? { unpaid_months: unpaidM } : {}),
                };
              }
            } catch {
              /* stats membre optionnelles */
            }
          }
          setPersonalStats(memberPayload);
          const profileIdForAssistance = resolveMemberProfileId(memberPayload, user);
          if (profileIdForAssistance) {
            try {
              const DASH_ASSISTANCE_LIMIT = 100;
              const asRes = await assistanceAPI.getCases({
                limit: DASH_ASSISTANCE_LIMIT,
                skip: 0,
                member_profile_id: profileIdForAssistance,
              });
              let rows = asRes?.data?.assistance_cases || [];
              if (rows.length === 0) {
                const alt = resolveNextMemberProfileId(memberPayload, user, profileIdForAssistance);
                if (alt) {
                  try {
                    const asRes2 = await assistanceAPI.getCases({
                      limit: DASH_ASSISTANCE_LIMIT,
                      skip: 0,
                      member_profile_id: alt,
                    });
                    rows = asRes2?.data?.assistance_cases || [];
                  } catch {
                    /* ignore second try */
                  }
                }
              }
              setMemberAssistanceCases(rows);
            } catch (e) {
              console.warn('Home: chargement des allocations (membre):', e);
              setMemberAssistanceCases([]);
            } finally {
              setMemberAssistanceLoading(false);
            }
          } else {
            setMemberAssistanceCases([]);
            setMemberAssistanceLoading(false);
          }
          const base = financialResponse.data || {};
          const byType = (ledgerStatsRes.data?.by_tx_type || []).reduce((acc, row) => {
            const k = String(row.tx_type ?? row._id ?? '').toLowerCase();
            if (!k) return acc;
            acc[k] = Number(row.total || 0);
            return acc;
          }, {});
          const merged = {
            ...base,
            total_members: Number(base.total_members ?? peopleRes.data?.total ?? 0),
            active_members: Number(base.active_members ?? peopleRes.data?.active ?? 0),
            total_balance: Number(base.total_balance ?? ledgerBalRes.data?.current_balance ?? 0),
            total_contributions: Number(base.total_contributions ?? byType.credit ?? 0),
            total_income: Number(base.total_income ?? byType.credit ?? 0),
            total_expenses: Number(base.total_expenses ?? byType.debit ?? 0),
          };
          const memberActs = getList(agoraActivitiesRes.data, 'activities')
            .filter((a) => {
              const t = new Date(a.scheduled_at || a.planned_date || a.date || a.created_at || 0).getTime();
              return Number.isFinite(t) && t >= Date.now();
            })
            .sort((a, b) => {
              const da = new Date(a.scheduled_at || a.planned_date || a.date || a.created_at || 0).getTime();
              const db = new Date(b.scheduled_at || b.planned_date || b.date || b.created_at || 0).getTime();
              return da - db;
            });
          setMemberInternalActivities(memberActs);
          setStats(merged);
        } else {
          // Tableau de bord : stats Entrea + People + Ledgera + cotisations + Agora + messagerie
          const [dashboardRes, peopleRes, ledgerBalRes, ledgerStatsRes, thisMonthRes, prevMonthRes, pendingRes, agoraActivitiesRes, messagingNotifRes] = await Promise.all([
            dashboardAPI.getStats(),
            peoplePlatformAPI.getStatsSummary().catch(() => ({ data: {} })),
            ledgerApi.getOrgBalance().catch(() => ({ data: {} })),
            ledgerApi.getStatsSummary().catch(() => ({ data: {} })),
            contributionsAPI.getAll({ month: thisMonth, status: 'completed', limit: 1000 }).catch(() => ({ data: {} })),
            contributionsAPI.getAll({ month: prevMonth, status: 'completed', limit: 1000 }).catch(() => ({ data: {} })),
            contributionsAPI.getAll({ status: 'pending', limit: 1000 }).catch(() => ({ data: {} })),
            agoraPlatformAPI.listActivities({ limit: 200 }).catch(() => ({ data: {} })),
            messagingPlatformAPI.listNotifications({ limit: 200, unread_only: true }).catch(() => ({ data: {} })),
          ]);

          const base = dashboardRes.data || {};
          const byType = (ledgerStatsRes.data?.by_tx_type || []).reduce((acc, row) => {
            const k = String(row.tx_type ?? row._id ?? '').toLowerCase();
            if (!k) return acc;
            acc[k] = Number(row.total || 0);
            return acc;
          }, {});
          const thisMonthList = getList(thisMonthRes.data, 'contributions');
          const prevMonthList = getList(prevMonthRes.data, 'contributions');
          const pendingList = getList(pendingRes.data, 'contributions');
          const activities = getList(agoraActivitiesRes.data, 'activities');
          const nowTs = new Date().getTime();
          const upcomingMeetings = activities.filter((a) => {
            const t = new Date(
              a.scheduled_at || a.planned_date || a.date || a.created_at || 0
            ).getTime();
            return Number.isFinite(t) && t >= nowTs;
          }).length;
          const unreadNotifs = getList(messagingNotifRes.data, 'notifications');
          const thisMonthTotal = thisMonthList.reduce((s, c) => s + Number(c.amount || 0), 0);
          const prevMonthTotal = prevMonthList.reduce((s, c) => s + Number(c.amount || 0), 0);
          const paidCount = thisMonthList.length;
          const pendingCount = pendingList.length;
          const paymentRate = paidCount + pendingCount > 0
            ? Number(((paidCount / (paidCount + pendingCount)) * 100).toFixed(1))
            : null;

          const merged = {
            ...base,
            total_members: Number(base.total_members ?? peopleRes.data?.total ?? 0),
            active_members: Number(base.active_members ?? peopleRes.data?.active ?? 0),
            total_balance: Number(base.total_balance ?? ledgerBalRes.data?.current_balance ?? 0),
            total_contributions: Number(base.total_contributions ?? byType.credit ?? 0),
            total_income: Number(base.total_income ?? byType.credit ?? 0),
            total_expenses: Number(base.total_expenses ?? byType.debit ?? 0),
            contributions_current_month: Number(base.contributions_current_month ?? thisMonthTotal),
            contributions_last_month: Number(base.contributions_last_month ?? prevMonthTotal),
            average_contribution: Number(
              base.average_contribution ?? (paidCount > 0 ? thisMonthTotal / paidCount : 0)
            ),
            unpaid_contributions_count: Number(base.unpaid_contributions_count ?? pendingCount),
            members_with_unpaid: Number(base.members_with_unpaid ?? pendingCount),
            payment_rate: base.payment_rate ?? paymentRate,
            contribution_rate: base.contribution_rate ?? paymentRate,
            upcoming_meetings: Number(base.upcoming_meetings ?? upcomingMeetings),
            pending_transactions_count: Number(base.pending_transactions_count ?? unreadNotifs.length),
          };
          setStats(merged);
        }
      } catch (error) {
        console.error('Failed to load dashboard stats:', error);
        if (useMemberPortalHome) {
          setMemberAssistanceCases([]);
          setMemberAssistanceLoading(false);
        }
      } finally {
        setLoading(false);
      }
    };

    loadStats();
  }, [useMemberPortalHome, user?.id, user?.person_id, user?.member_id]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  // Calculate comparison with last month
  const contributionChange = stats?.contributions_current_month && stats?.contributions_last_month
    ? ((stats.contributions_current_month - stats.contributions_last_month) / stats.contributions_last_month * 100).toFixed(1)
    : null;

  const memberStatsFallback = {
    member_name: user?.name || t('memberPortal.member'),
    member_id: user?.member_id || user?.person_id || null,
    person_id: user?.person_id || user?.member_id || null,
    total_contributions: Number(stats?.total_contributions || 0),
    adhesion_fees: Number(stats?.adhesion_fees ?? stats?.membership_fees ?? 0),
    regular_contributions: Number(
      stats?.regular_contributions ??
        Math.max(0, Number(stats?.total_contributions || 0) - Number(stats?.adhesion_fees ?? 0))
    ),
    unpaid_count: Number(stats?.unpaid_contributions_count || 0),
    unpaid_total: Number(stats?.unpaid_total || 0),
    pending_requests_count: Number(stats?.unpaid_contributions_count || 0),
    pending_requests_total: Number(stats?.unpaid_total || 0),
    last_payment: null,
    unpaid_contributions: [],
    contribution_history: [],
  };

  // Membre + officers liés : entrée via portail membre
  if (useMemberPortalHome) {
    const memberStats = personalStats || memberStatsFallback;
  return (
      <div className="space-y-2.5 sm:space-y-4">
        <MemberDashboard
          personalStats={memberStats}
          globalStats={stats}
          internalActivities={memberInternalActivities}
          assistanceDataFromParent
          assistanceCases={memberAssistanceCases}
          assistanceParentLoading={memberAssistanceLoading}
        />
      </div>
    );
  }

  return (
    <div className="space-y-3 sm:space-y-4">
      {/* Quick Access Icons - Mobile Only */}
      <div className="md:hidden bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <h2 className="text-sm font-semibold text-gray-900 mb-3">{t('home.quickAccess')}</h2>
        <div className="grid grid-cols-3 gap-3">
          {filteredQuickAccess.map((item) => {
            const colorClasses = {
              indigo: 'bg-indigo-50 text-indigo-600',
              blue: 'bg-blue-50 text-blue-600',
              emerald: 'bg-emerald-50 text-emerald-600',
              green: 'bg-green-50 text-green-600',
              purple: 'bg-purple-50 text-purple-600',
              pink: 'bg-pink-50 text-pink-600',
            };
            const isCotisations = item.id === 'contributions';
            return (
              <Link
                key={item.id}
                to={item.href}
                className={`flex flex-col items-center justify-center rounded-xl hover:bg-gray-50 transition-all ${
                  isCotisations 
                    ? 'p-3.5 border-2 border-emerald-400 bg-gradient-to-br from-emerald-50 to-green-50 shadow-md' 
                    : 'p-3'
                }`}
              >
                <div className={`${isCotisations ? 'w-14 h-14' : 'w-12 h-12'} rounded-xl ${colorClasses[item.color]} ${isCotisations ? 'shadow-lg' : ''} flex items-center justify-center mb-2`}>
                  <svg className={`${isCotisations ? 'w-7 h-7' : 'w-6 h-6'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d={item.icon} />
                  </svg>
                </div>
                <span className={`text-xs font-medium text-center ${isCotisations ? 'text-gray-900 font-semibold' : 'text-gray-700'}`}>{item.name}</span>
              </Link>
            );
          })}
        </div>
      </div>
      {/* Quick Stats - Only show if not member */}
      {!isMember && (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <div className="bg-gradient-to-br from-white via-indigo-50/30 to-purple-50/30 md:bg-white rounded-2xl md:rounded-xl shadow-xl md:shadow-sm border-0 md:border border-gray-200 p-5 sm:p-5 hover:shadow-2xl md:hover:shadow-md transition-all backdrop-blur-sm md:backdrop-blur-none">
            <div className="flex items-center justify-between mb-3">
            <div className="p-3 sm:p-2.5 bg-gradient-to-br from-indigo-500 to-purple-600 md:bg-indigo-50 rounded-2xl md:rounded-lg shadow-lg shadow-indigo-500/30 md:shadow-none">
              <svg className="h-6 w-6 sm:h-6 sm:w-6 text-white md:text-indigo-600" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z"/>
                </svg>
              </div>
            </div>
          <p className="text-gray-500 md:text-gray-500 text-xs uppercase tracking-wide mb-2">{t('home.totalMembers')}</p>
          <h3 className="text-3xl sm:text-4xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent md:text-gray-900 mb-2">{stats?.total_members || 0}</h3>
            <div className="flex items-center gap-2">
            <span className="px-2.5 py-1 bg-green-100 text-green-700 rounded-full text-xs font-bold">
                {t('home.activeMembers', { count: stats?.active_members || 0 })}
              </span>
          </div>
        </div>

        <div className="bg-gradient-to-br from-white via-emerald-50/30 to-teal-50/30 md:bg-white rounded-2xl md:rounded-xl shadow-xl md:shadow-sm border-0 md:border border-gray-200 p-5 sm:p-5 hover:shadow-2xl md:hover:shadow-md transition-all backdrop-blur-sm md:backdrop-blur-none">
            <div className="flex items-center justify-between mb-3">
            <div className="p-3 sm:p-2.5 bg-gradient-to-br from-emerald-500 to-teal-600 md:bg-emerald-50 rounded-2xl md:rounded-lg shadow-lg shadow-emerald-500/30 md:shadow-none">
              <svg className="h-6 w-6 sm:h-6 sm:w-6 text-white md:text-emerald-600" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M8.433 7.418c.155-.103.346-.196.567-.267v1.698a2.305 2.305 0 01-.567-.267C8.07 8.34 8 8.114 8 8c0-.114.07-.34.433-.582zM11 12.849v-1.698c.22.071.412.164.567.267.364.243.433.468.433.582 0 .114-.07.34-.433.582a2.305 2.305 0 01-.567.267z"/>
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a1 1 0 10-2 0v.092a4.535 4.535 0 00-1.676.662C6.602 6.234 6 7.009 6 8c0 .99.602 1.765 1.324 2.246.48.32 1.054.545 1.676.662v1.941c-.391-.127-.68-.317-.843-.504a1 1 0 10-1.51 1.31c.562.649 1.413 1.076 2.353 1.253V15a1 1 0 102 0v-.092a4.535 4.535 0 001.676-.662C13.398 13.766 14 12.991 14 12c0-.99-.602-1.765-1.324-2.246A4.535 4.535 0 0011 9.092V7.151c.391.127.68.317.843.504a1 1 0 101.511-1.31c-.563-.649-1.413-1.076-2.354-1.253V5z" clipRule="evenodd"/>
                </svg>
              </div>
            </div>
          <p className="text-gray-500 md:text-gray-500 text-xs uppercase tracking-wide mb-2">{t('home.currentBalance')}</p>
          <h3 className="text-3xl sm:text-4xl font-bold bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent md:text-gray-900 mb-2">
            {(stats?.total_balance || 0).toLocaleString(localeFmt)} <span className="text-lg text-gray-500 font-normal">FBU</span>
            </h3>
        </div>

        <div className="bg-gradient-to-br from-white via-blue-50/30 to-cyan-50/30 md:bg-white rounded-2xl md:rounded-lg shadow-xl md:shadow-sm border-0 md:border border-gray-200 p-5 md:p-6 hover:shadow-2xl md:hover:shadow-md transition-all backdrop-blur-sm md:backdrop-blur-none">
            <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-gradient-to-br from-blue-500 to-cyan-600 md:bg-blue-50 rounded-2xl md:rounded-lg shadow-lg shadow-blue-500/30 md:shadow-none">
              <svg className="h-6 w-6 text-white md:text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd"/>
                </svg>
              </div>
            </div>
          <p className="text-gray-500 md:text-gray-500 text-xs font-medium tracking-wide mb-2">{t('home.upcomingMeetings')}</p>
          <h3 className="text-3xl sm:text-4xl font-bold bg-gradient-to-r from-blue-600 to-cyan-600 bg-clip-text text-transparent md:text-gray-900 mb-2">{stats?.upcoming_meetings || 0}</h3>
          <p className="text-gray-500 text-sm">{t('home.plannedEventsHint')}</p>
        </div>

        <div className="bg-gradient-to-br from-white via-purple-50/30 to-pink-50/30 md:bg-white rounded-2xl md:rounded-lg shadow-xl md:shadow-sm border-0 md:border border-gray-200 p-5 md:p-6 hover:shadow-2xl md:hover:shadow-md transition-all backdrop-blur-sm md:backdrop-blur-none">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-gradient-to-br from-purple-500 to-pink-600 md:bg-purple-50 rounded-2xl md:rounded-lg shadow-lg shadow-purple-500/30 md:shadow-none">
              <svg className="h-6 w-6 text-white md:text-purple-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
              </svg>
            </div>
          </div>
          <p className="text-gray-500 md:text-gray-500 text-xs font-medium tracking-wide mb-2">{t('home.recoveryRate')}</p>
          <h3 className="text-3xl sm:text-4xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent md:text-gray-900 mb-2">
            {stats?.contribution_rate !== null && stats?.contribution_rate !== undefined ? `${stats.contribution_rate}%` : t('home.notApplicable')}
          </h3>
          {stats?.members_with_unpaid !== undefined && (
            <p className="text-gray-500 text-sm">{t('home.membersOverdue', { count: stats.members_with_unpaid })}</p>
          )}
        </div>
      </div>
      )}

      {/* Financial Metrics Cards — piloté par permissions catalogue */}
      {canSeeOrgFinancialDashboard && (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-base font-semibold text-gray-900">{t('home.monthContributions')}</h3>
            {contributionChange && (
              <span className={`text-xs font-medium ${contributionChange >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {contributionChange >= 0 ? '↑' : '↓'} {Math.abs(contributionChange)}%
              </span>
            )}
          </div>
          <p className="text-2xl sm:text-3xl font-semibold text-gray-900">
            {(stats?.contributions_current_month || 0).toLocaleString(localeFmt)} <span className="text-base text-gray-500">FBU</span>
          </p>
          {stats?.contributions_last_month && (
            <p className="text-sm text-gray-500 mt-2">
              {t('home.previousMonth', {
                amount: stats.contributions_last_month.toLocaleString(localeFmt),
              })}
            </p>
          )}
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6">
          <h3 className="text-base font-semibold text-gray-900 mb-3">{t('home.avgPerContribution')}</h3>
          <p className="text-2xl sm:text-3xl font-semibold text-gray-900">
            {stats?.average_contribution ? stats.average_contribution.toLocaleString(localeFmt) : '0'} <span className="text-base text-gray-500">FBU</span>
          </p>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6">
          <h3 className="text-base font-semibold text-gray-900 mb-4">{t('home.financialSplit')}</h3>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-500">{t('home.splitInflows')}</span>
              <span className="text-base font-semibold text-gray-900">{(stats?.total_contributions || 0).toLocaleString(localeFmt)} FBU</span>
              </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-500">{t('home.splitOutflows')}</span>
              <span className="text-base font-semibold text-gray-900">{(stats?.total_expenses || 0).toLocaleString(localeFmt)} FBU</span>
            </div>
          </div>
        </div>
      </div>
      )}

      {/* Charts Section — piloté par permissions catalogue */}
      {canSeeOrgFinancialDashboard && (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Monthly Financial Trends */}
        {stats?.monthly_financials && stats.monthly_financials.length > 0 && (
          <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('home.financialTrend')}</h2>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={stats.monthly_financials}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip formatter={(value) => `${Number(value).toLocaleString(localeFmt)} FBU`} />
                <Legend />
                <Line type="monotone" dataKey="contributions" stroke="#10b981" strokeWidth={2} name={t('home.splitInflows')} />
                <Line type="monotone" dataKey="expenses" stroke="#ef4444" strokeWidth={2} name={t('home.splitOutflows')} />
                <Line type="monotone" dataKey="balance" stroke="#6366f1" strokeWidth={2} name={t('home.chartBalance')} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Members Distribution by Commission */}
        {stats?.members_by_commission && Object.keys(stats.members_by_commission).length > 0 && (
          <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('home.splitByCommission')}</h2>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={Object.entries(stats.members_by_commission).map(([name, value]) => ({ name, value }))}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {Object.keys(stats.members_by_commission).map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
      )}

      {/* Role-Specific Widgets — permissions plutôt que libellés de rôle */}
      {canSeeTreasurerWidget && (
        <TreasurerFinancialWidget stats={stats} />
      )}
      {canSeeSecretaryWidget && (
        <SecretaryWidget stats={stats} />
      )}
      {canSeePresidentOverviewWidget && (
        <PresidentOverviewWidget stats={stats} />
      )}

      {/* Trends and Contribution Stats */}
      {canSeeOrgFinancialDashboard && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <TrendsWidget stats={stats} />
          <ContributionsStatsWidget stats={stats} />
        </div>
      )}

      {canSeeOrgFinancialDashboard && (
        <div className="mt-6 max-w-xl">
          <LedgerPlatformWidget />
        </div>
      )}

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Link
          to="/calendar"
          className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow"
        >
            <div className="mb-4">
            <div className="inline-flex p-3 bg-indigo-50 rounded-lg">
              <svg className="h-6 w-6 text-indigo-600" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z"/>
                  <path fillRule="evenodd" d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3zm-3 4a1 1 0 100 2h.01a1 1 0 100-2H7zm3 0a1 1 0 100 2h3a1 1 0 100-2h-3z" clipRule="evenodd"/>
                </svg>
              </div>
            </div>
          <h3 className="text-base font-semibold text-gray-900 mb-2">{t('home.cardCalendarTitle')}</h3>
          <p className="text-gray-600 text-sm">
              {t('home.cardCalendarDesc')}
            </p>
        </Link>

        <Link
          to="/members"
          className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow"
        >
            <div className="mb-4">
            <div className="inline-flex p-3 bg-emerald-50 rounded-lg">
              <svg className="h-6 w-6 text-emerald-600" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z"/>
              </svg>
            </div>
          </div>
          <h3 className="text-lg font-bold text-gray-900 mb-2">{t('home.cardMembersTitle')}</h3>
          <p className="text-gray-600 text-sm">
            {t('home.cardMembersDesc')}
          </p>
        </Link>

        <Link
          to="/my-fund"
          className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow"
        >
            <div className="mb-4">
            <div className="inline-flex p-3 bg-blue-50 rounded-lg">
              <svg className="h-6 w-6 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M8.433 7.418c.155-.103.346-.196.567-.267v1.698a2.305 2.305 0 01-.567-.267C8.07 8.34 8 8.114 8 8c0-.114.07-.34.433-.582zM11 12.849v-1.698c.22.071.412.164.567.267.364.243.433.468.433.582 0 .114-.07.34-.433.582a2.305 2.305 0 01-.567.267z"/>
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a1 1 0 10-2 0v.092a4.535 4.535 0 00-1.676.662C6.602 6.234 6 7.009 6 8c0 .99.602 1.765 1.324 2.246.48.32 1.054.545 1.676.662v1.941c-.391-.127-.68-.317-.843-.504a1 1 0 10-1.51 1.31c.562.649 1.413 1.076 2.353 1.253V15a1 1 0 102 0v-.092a4.535 4.535 0 001.676-.662C13.398 13.766 14 12.991 14 12c0-.99-.602-1.765-1.324-2.246A4.535 4.535 0 0011 9.092V7.151c.391.127.68.317.843.504a1 1 0 101.511-1.31c-.563-.649-1.413-1.076-2.354-1.253V5z" clipRule="evenodd"/>
                </svg>
              </div>
            </div>
          <h3 className="text-lg font-bold text-gray-900 mb-2">{t('home.cardOrgFundTitle')}</h3>
          <p className="text-gray-600 text-sm">
              {t('home.cardOrgFundDesc')}
            </p>
        </Link>

        <Link
          to="/documents"
          className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow"
        >
            <div className="mb-4">
            <div className="inline-flex p-3 bg-purple-50 rounded-lg">
              <svg className="h-6 w-6 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd"/>
              </svg>
            </div>
          </div>
          <h3 className="text-lg font-bold text-gray-900 mb-2">{t('home.cardDocumentsTitle')}</h3>
          <p className="text-gray-600 text-sm">
            {t('home.cardDocumentsDesc')}
          </p>
        </Link>
      </div>

      {/* Recent Activities */}
      {stats?.recent_activities && stats.recent_activities.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="bg-gray-50 border-b border-gray-200 p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-indigo-100 rounded-lg">
                    <svg className="h-5 w-5 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">{t('home.recentActivityTitle')}</h2>
                    <p className="text-gray-600 text-sm">{t('home.recentActivitySubtitle')}</p>
                  </div>
                </div>
                <div className="bg-gray-200 px-3 py-1 rounded-lg">
                  <span className="text-gray-700 font-semibold text-sm">{stats.recent_activities.length}</span>
                </div>
              </div>
          </div>
          <div className="p-6">
            <div className="space-y-4">
              {stats.recent_activities.slice(0, 5).map((activity, index) => {
                const activityDate = new Date(activity.date);
                const isToday = activityDate.toDateString() === new Date().toDateString();
                const isYesterday = activityDate.toDateString() === new Date(Date.now() - 86400000).toDateString();
                
                let dateDisplay = activity.date;
                if (isToday) {
                  dateDisplay = t('home.activityTodayAt', {
                    time: activityDate.toLocaleTimeString(localeFmt, { hour: '2-digit', minute: '2-digit' }),
                  });
                } else if (isYesterday) {
                  dateDisplay = t('home.activityYesterdayAt', {
                    time: activityDate.toLocaleTimeString(localeFmt, { hour: '2-digit', minute: '2-digit' }),
                  });
                } else {
                  dateDisplay = activityDate.toLocaleDateString(localeFmt, { 
                    day: 'numeric', 
                    month: 'short', 
                    year: activityDate.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined,
                    hour: '2-digit',
                    minute: '2-digit'
                  });
                }

                const isTransaction = activity.type === 'transaction';
                const iconBgColor = isTransaction 
                  ? 'bg-emerald-50' 
                  : 'bg-blue-50';
                const iconColor = isTransaction ? 'text-emerald-600' : 'text-blue-600';
                const badgeColor = isTransaction 
                  ? 'bg-emerald-100 text-emerald-700' 
                  : 'bg-blue-100 text-blue-700';

                return (
                  <div 
                    key={index} 
                    className="flex items-center gap-4 p-4 border-b border-gray-100 last:border-b-0 hover:bg-gray-50 transition-colors"
                  >
                    {/* Icon */}
                    <div className="flex-shrink-0">
                      <div className={`p-2 rounded-lg ${iconBgColor}`}>
                        {isTransaction ? (
                          <svg className={`h-4 w-4 ${iconColor}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    ) : (
                          <svg className={`h-4 w-4 ${iconColor}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    )}
                  </div>
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${badgeColor}`}>
                              {isTransaction ? t('home.activityTypeTransaction') : t('home.activityTypeMeeting')}
                            </span>
                          </div>
                          <p className="text-sm text-gray-900 line-clamp-2">
                            {activity.description}
                          </p>
                        </div>
                        <div className="flex-shrink-0 text-right">
                          <p className="text-xs text-gray-500 whitespace-nowrap">
                            {dateDisplay}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
                </div>
            {stats.recent_activities.length > 5 && (
              <div className="mt-6 pt-4 border-t border-gray-200 text-center">
                <Link
                  to="/my-fund/financial"
                  className="inline-flex items-center text-sm font-medium text-indigo-600 hover:text-indigo-700 transition-colors"
                >
                  {t('home.viewAllActivity')}
                  <svg className="ml-2 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </Link>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Home;